/**
 * Course Access Service
 *
 * Handles loading course structure and updating package access.
 * Supports both mock data (for development) and real API calls.
 */

import { $http } from '@/utils/https'
import { mockCourseStructure } from '@/mocks/courseStructureMock'

// Toggle between mock and real API
// Set to false when ready to use real backend
const USE_MOCK = false

// Material type mapping (backend numeric types to string types)
const MATERIAL_TYPES = {
	1: 'video',
	2: 'text',
	3: 'audio',
	4: 'image',
	5: 'pdf',
	6: 'file'
}

export default {
	/**
	* Load complete course structure with access flags
	*
	* @param {number} courseId - Course ID
	* @param {number} packageId - Package ID
	* @returns {Promise<Object>} Course structure with modules, lessons, materials, and access flags
	*/
	async getCourseStructureWithAccess(courseId, packageId) {
		if (USE_MOCK) {
			console.log('🎭 Using MOCK data for course structure')
			console.log('📦 Course ID:', courseId, '| Package ID:', packageId)
			
			// Simulate network delay
			return new Promise(resolve => {
				setTimeout(() => {
					console.log('✅ Mock data loaded successfully')
					resolve({ data: mockCourseStructure })
				}, 500)
			})
		}
		
		console.log('🌐 Loading course structure from API...')
		console.log('📦 Course ID:', courseId, '| Package ID:', packageId)
		
		// /admin/materials/{courseId}/{packageId}
		
		try {
			// Step 1: Get course with modules
			const courseUrl = `${process.env.VUE_APP_ROOT_COURSE}courses/${courseId}`
			console.log('📡 Fetching course:', courseUrl)
			
			const courseRes = await $http.get(courseUrl)
			
			// Step 2: Get package with current access array
			const packageUrl = `${process.env.VUE_APP_ROOT_COURSE}courses/${courseId}/packages/${packageId}`
			console.log('📡 Fetching package:', packageUrl)
			
			const packageRes = await $http.get(packageUrl)
			
			const packageData = packageRes.data.data || packageRes.data
			const currentAccess = packageData.access || []
			
			console.log('✅ Package loaded:', packageData.name)
			console.log('📊 Current access count:', currentAccess.length)
			
			// Step 3: Load all modules → lessons → materials
			const courseData = courseRes.data.data || courseRes.data
			const modulesData = courseData.modules?.data || courseData.modules || []
			
			console.log('📚 Loading', modulesData.length, 'modules...')
			
			const modules = await Promise.all(
				modulesData.map(async (module) => {
					// Get lessons for this module
					const lessonsUrl = `${process.env.VUE_APP_ROOT_COURSE}modules/${module.id}/lessons`
					const lessonsRes = await $http.get(lessonsUrl)
					
					const lessonsData = lessonsRes.data.data || lessonsRes.data || []
					
					// Get materials for each lesson
					const lessons = await Promise.all(
						lessonsData.map(async (lesson) => {
							const lessonUrl = `${process.env.VUE_APP_ROOT_COURSE}lessons/${lesson.id}`
							const lessonRes = await $http.get(lessonUrl)
							
							const lessonData = lessonRes.data.data || lessonRes.data
							
							// Map education materials with hasAccess flag
							const materials = (lessonData.education_materials?.data || lessonData.educationMaterials?.data || []).map(m => ({
								id			: m.id,
								name		: m.name,
								type		: MATERIAL_TYPES[m.type] || 'file',
								order		: m.order || 0,
								hasAccess	: currentAccess.includes(m.id)
							}))
							
							// Map homeworks with hasAccess flag
							const homeworks = (lessonData.homeworks?.data || lessonData.homeworks || []).map(hw => ({
								id			: hw.id,
								name		: hw.name,
								order		: hw.order || 0,
								hasAccess	: currentAccess.includes(hw.id)
							}))
							
							return {
								id: lesson.id,
								name: lesson.name,
								materials,
								homeworks
							}
						})
					)
					
					return {
						id: module.id,
						name: module.name,
						lessons
					}
				})
			)
			
			const result = {
				courseId,
				packageId,
				packageName: packageData.name,
				currentAccess,
				modules
			}
			
			// Log statistics
			let totalMaterials = 0;
			let totalHomeworks = 0;
			
			modules.forEach(m => {
				m.lessons.forEach(l => {
					totalMaterials += l.materials.length
					totalHomeworks += l.homeworks.length
				})
			})
			
			console.log('✅ Course structure loaded successfully')
			console.log('📊 Statistics:', {
				modules: modules.length,
				lessons: modules.reduce((sum, m) => sum + m.lessons.length, 0),
				materials: totalMaterials,
				homeworks: totalHomeworks,
				withAccess: currentAccess.length
			})
			
			return { data: result }
		} catch (error) {
			console.error('❌ Error loading course structure:', error)
			
			// Provide meaningful error messages
			let message = 'Failed to load course structure'
			
			if (error.response) {
				// Server responded with error status
				const status = error.response.status
				
				if (status === 404) {
					message = 'Course or package not found'
				} else if (status === 403) {
					message = 'You do not have permission to access this data'
				} else if (status === 401) {
					message = 'Authentication required. Please log in again.'
				} else if (status === 500) {
					message = 'Server error. Please try again later'
				} else if (error.response.data?.error?.message) {
					message = error.response.data.error.message
				}
			} else if (error.request) {
				// Request made but no response
				message = 'Network error. Please check your connection'
			}
			
			const enhancedError = new Error(message)
			
			enhancedError.originalError = error
			
			throw enhancedError
		}
	},
	
	/**
	* Update package access array (bulk update)
	*
	* @param {number} courseId - Course ID
	* @param {number} packageId - Package ID
	* @param {number[]} selectedMaterialIds - Array of material IDs that should have access
	* @returns {Promise<Object>} Update result
	*/
	async bulkUpdateAccess(courseId, packageId, selectedMaterialIds) {
		if (USE_MOCK) {
			console.log('🎭 MOCK: Updating package access')
			console.log('📦 Course ID:', courseId, '| Package ID:', packageId)
			console.log('📊 Selected materials:', selectedMaterialIds.length)
			console.log('🔢 Material IDs:', selectedMaterialIds)
			
			// Simulate network delay
			return new Promise(resolve => {
				setTimeout(() => {
					console.log('✅ MOCK: Access updated successfully')
					
					resolve({
						data: {
							success	: true,
							access	: selectedMaterialIds,
							updated	: selectedMaterialIds.length
						}
					})
				}, 1000)
			})
		}
		
		console.log('🌐 Updating package access via API...')
		console.log('📦 Package ID:', packageId)
		console.log('📊 New access count:', selectedMaterialIds.length)
		
		try {
			const url = `${process.env.VUE_APP_ROOT_COURSE}courses/${courseId}/packages/${packageId}`
			
			const response = await $http.patch(url, {
				access: selectedMaterialIds
			})
			
			console.log('✅ Access updated successfully')
			
			return response
		} catch (error) {
			console.error('❌ Error updating access:', error)
			
			// Provide meaningful error messages
			let message = 'Failed to update access'
			
			if (error.response) {
				const status = error.response.status
				
				if (status === 404) {
					message = 'Package not found'
				} else if (status === 403) {
					message = 'You do not have permission to update this package'
				} else if (status === 401) {
					message = 'Authentication required. Please log in again.'
				} else if (status === 422) {
					message = 'Invalid data. Please check your selections.'
				} else if (status === 500) {
					message = 'Server error. Please try again later'
				} else if (error.response.data?.error?.message) {
					message = error.response.data.error.message
				}
			} else if (error.request) {
				message = 'Network error. Please check your connection'
			}
			
			const enhancedError = new Error(message)
			
			enhancedError.originalError = error
			
			throw enhancedError
		}
	},
	
	/**
	* Get current mock mode status
	* @returns {boolean} True if using mock data
	*/
	isUsingMock() {
		return USE_MOCK
	}
}
